import type { UserListItem, UserRole, UserStatus } from '@/api/models';

/**
 * Presentational config + pure role/matrix helpers for the Team (users) table.
 * Everything here is React-free so the create-user matrix, PIN validation, and
 * column-scoping rules are unit-testable in isolation.
 */

export type User = UserListItem;

export type UserRowAction = 'edit' | 'setPin' | 'resetPassword' | 'delete';

/** Stable column ids — shared between the View columns and sort descriptor. */
export const USER_COLUMN = {
    name: 'name',
    email: 'email',
    role: 'role',
    branch: 'branch',
    status: 'status',
    hasPin: 'hasPin',
    actions: 'actions',
} as const;

/** All roles, in display order (most-privileged first). */
export const USER_ROLES: UserRole[] = ['OrgAdmin', 'BranchAdmin', 'Seller'];

export const ROLE_LABEL_KEY: Record<UserRole, string> = {
    OrgAdmin: 'team.roles.orgAdmin',
    BranchAdmin: 'team.roles.branchAdmin',
    Seller: 'team.roles.seller',
};

export const STATUS_LABEL_KEY: Record<UserStatus, string> = {
    Active: 'team.status.active',
    Disabled: 'team.status.disabled',
};

/** Which form fields a given role exposes in the create modal. */
export type RoleFields = {
    name: boolean;
    branch: boolean;
    email: boolean;
    password: boolean;
    pin: boolean;
};

/**
 * The create-user field matrix (mirrors the server contract):
 * - **Seller** — name + branch (+ optional pin).
 * - **BranchAdmin** — name + branch + email + password (+ optional pin).
 * - **OrgAdmin** — name + email + password.
 */
export const getRoleFields = (role: UserRole): RoleFields => {
    switch (role) {
        case 'Seller':
            return {
                name: true,
                branch: true,
                email: false,
                password: false,
                pin: true,
            };
        case 'BranchAdmin':
            return {
                name: true,
                branch: true,
                email: true,
                password: true,
                pin: true,
            };
        case 'OrgAdmin':
            return {
                name: true,
                branch: false,
                email: true,
                password: true,
                pin: false,
            };
    }
};

/**
 * Roles the caller may assign when creating a user. An OrgAdmin can create any
 * role; a BranchAdmin can create only Sellers (in their own branch); a Seller
 * has no management access at all.
 */
export const getAssignableRoles = (callerRole: UserRole): UserRole[] => {
    switch (callerRole) {
        case 'OrgAdmin':
            return ['OrgAdmin', 'BranchAdmin', 'Seller'];
        case 'BranchAdmin':
            return ['Seller'];
        case 'Seller':
            return [];
    }
};

/** A BranchAdmin is pinned to their own branch; only an OrgAdmin picks one. */
export const isBranchLocked = (callerRole: UserRole): boolean =>
    callerRole === 'BranchAdmin';

/** Branch/role reassignment on edit is OrgAdmin-only. */
export const canReassignBranchOrRole = (callerRole: UserRole): boolean =>
    callerRole === 'OrgAdmin';

/** The branch column is only meaningful when the caller sees multiple branches. */
export const shouldShowBranchColumn = (callerRole: UserRole): boolean =>
    callerRole === 'OrgAdmin';

/** A PIN is exactly 4 digits. */
export const isValidPin = (pin: string): boolean => /^\d{4}$/.test(pin);

/** A back-office password — minimum length mirrors the server's floor. */
export const MIN_PASSWORD_LENGTH = 8;
export const isValidPassword = (password: string): boolean =>
    password.length >= MIN_PASSWORD_LENGTH;

/**
 * Row actions available for a given user, from the caller's perspective:
 * - PIN reset only for PIN-bearing roles (Seller, BranchAdmin).
 * - Password reset only for back-office roles (BranchAdmin, OrgAdmin).
 * - Delete is hidden for the caller's own row (can't delete yourself).
 */
export const getUserRowActions = (
    row: User,
    callerId: string | null,
): UserRowAction[] => {
    const actions: UserRowAction[] = ['edit'];
    if (row.role === 'Seller' || row.role === 'BranchAdmin') {
        actions.push('setPin');
    }
    if (row.role === 'BranchAdmin' || row.role === 'OrgAdmin') {
        actions.push('resetPassword');
    }
    if (row.id !== callerId) {
        actions.push('delete');
    }
    return actions;
};

export const USER_ROW_ACTION_META: Record<
    UserRowAction,
    { icon: string; labelKey: string; variant?: 'danger' }
> = {
    edit: {
        icon: 'gravity-ui:pencil',
        labelKey: 'team.components.userTable.actions.edit',
    },
    setPin: {
        icon: 'gravity-ui:lock',
        labelKey: 'team.components.userTable.actions.setPin',
    },
    resetPassword: {
        icon: 'gravity-ui:key',
        labelKey: 'team.components.userTable.actions.resetPassword',
    },
    delete: {
        icon: 'gravity-ui:trash-bin',
        labelKey: 'team.components.userTable.actions.delete',
        variant: 'danger',
    },
};

/** Default rows shown per page in the users table (server `pageSize`). */
export const TEAM_PAGE_SIZE = 10;

/** Selectable page sizes for the users table's rows-per-page control. */
export const TEAM_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};
